import { useEffect, useState } from "react";
import { Loader2, Plus, Copy, Check, Trash2, RefreshCw, Code2, BookOpen, Activity, ExternalLink, ChevronRight, X } from "lucide-react";
import api, { formatDetail, fmtINR } from "@/lib/api";

/**
 * API & Webhooks — merged partner-facing page covering:
 *  - API keys (create / revoke)
 *  - Webhook URL & format config
 *  - Webhook delivery log (inline modal per payout)
 *  - Link to the public API docs page
 */
export default function PanelApi() {
    const [tab, setTab] = useState("keys");
    const [keys, setKeys] = useState([]);
    const [wallet, setWallet] = useState(null);
    const [newKey, setNewKey] = useState(null);
    const [keyName, setKeyName] = useState("");
    const [showKeyModal, setShowKeyModal] = useState(false);
    const [whUrl, setWhUrl] = useState("");
    const [whFmt, setWhFmt] = useState("paygateway");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [copied, setCopied] = useState("");
    const [recentPayouts, setRecentPayouts] = useState([]);
    const [deliveryModal, setDeliveryModal] = useState(null);  // {payout_id, items}

    const refresh = async () => {
        setErr("");
        try {
            const [k, w, p] = await Promise.all([
                api.get("/partner/payouts/api-keys"),
                api.get("/partner/payouts/wallet"),
                api.get("/partner/payouts?limit=20"),
            ]);
            setKeys(k.data.items || []);
            setWallet(w.data);
            setWhUrl(w.data.webhook_url || "");
            setWhFmt(w.data.webhook_format || "paygateway");
            setRecentPayouts(p.data.items || []);
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };
    useEffect(() => { refresh(); }, []);

    const createKey = async () => {
        if (!keyName.trim()) return;
        setBusy(true);
        try {
            const r = await api.post("/partner/payouts/api-keys", { name: keyName.trim() });
            setNewKey(r.data); setKeyName(""); setShowKeyModal(false); refresh();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    const revokeKey = async (id) => {
        if (!window.confirm("Revoke this API key? Any system using it will stop working immediately.")) return;
        try { await api.delete(`/partner/payouts/api-keys/${id}`); refresh(); }
        catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };

    const saveWebhook = async () => {
        setBusy(true);
        try {
            await api.patch("/partner/payouts/webhook-config", {
                webhook_url: whUrl.trim() || null, webhook_format: whFmt,
            });
            refresh();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    const rotateSecret = async () => {
        if (!window.confirm("Rotate webhook secret? Your receiver will need to update the HMAC key.")) return;
        try {
            const r = await api.post("/partner/payouts/webhook-config/rotate-secret");
            alert(`New webhook secret:\n\n${r.data.webhook_secret}\n\nStore it securely — it won't be shown again.`);
            refresh();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };

    const openDeliveries = async (payoutId) => {
        try {
            const r = await api.get(`/partner/payouts/${payoutId}/webhook-deliveries`);
            setDeliveryModal({ payout_id: payoutId, items: r.data.items || [] });
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };

    const copy = async (text, key) => {
        try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(""), 1500); } catch {}
    };

    return (
        <div data-testid="panel-api" className="p-6 space-y-5">
            <div>
                <h1 className="text-xl font-bold flex items-center gap-2"><Code2 className="w-5 h-5" /> API & Webhooks</h1>
                <p className="text-xs text-slate-500 mt-1">Generate keys, point webhooks, and track delivery status.</p>
            </div>

            {err && <div data-testid="api-err" className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-700">{err}</div>}

            {/* Tabs */}
            <div className="border-b border-slate-200 flex gap-6 overflow-x-auto">
                {[
                    ["keys", `API Keys (${keys.length})`],
                    ["webhook", "Webhook Config"],
                    ["deliveries", `Webhook Log`],
                    ["docs", "Documentation"],
                ].map(([k, label]) => (
                    <button key={k} data-testid={`api-tab-${k}`} onClick={() => setTab(k)}
                            className={`py-2 text-sm border-b-2 -mb-px whitespace-nowrap ${
                                tab === k ? "border-indigo-600 text-indigo-700 font-semibold" : "border-transparent text-slate-500"
                            }`}>{label}</button>
                ))}
            </div>

            {tab === "keys" && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <p className="text-xs text-slate-500">Pass as <code className="bg-slate-100 px-1 rounded">X-API-Key</code> header on every public API call.</p>
                        <button data-testid="api-key-new" onClick={() => setShowKeyModal(true)}
                                className="btn-primary px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5">
                            <Plus className="w-3.5 h-3.5" /> New API key
                        </button>
                    </div>

                    {newKey && (
                        <div data-testid="new-key-banner" className="bg-emerald-50 border border-emerald-200 rounded-md p-4">
                            <p className="text-xs font-semibold text-emerald-900">Your new API key — save it now, it won't be shown again:</p>
                            <div className="mt-2 flex items-center gap-2 bg-white border border-emerald-200 rounded p-2">
                                <code className="text-xs break-all flex-1 font-mono">{newKey.key}</code>
                                <button onClick={() => copy(newKey.key, "newkey")} className="text-slate-500 shrink-0">
                                    {copied === "newkey" ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                                </button>
                            </div>
                            <button onClick={() => setNewKey(null)} className="mt-2 text-xs text-slate-500 hover:underline">Done</button>
                        </div>
                    )}

                    <div className="bg-white border border-slate-200 rounded-xl">
                        {keys.length === 0 ? (
                            <div className="p-10 text-center text-xs text-slate-500">No API keys. Click <strong>New API key</strong>.</div>
                        ) : (
                            <ul className="divide-y divide-slate-100">
                                {keys.map((k) => (
                                    <li key={k.api_key_id} className="p-3 flex items-center justify-between" data-testid={`api-key-row-${k.api_key_id}`}>
                                        <div className="min-w-0">
                                            <div className="font-medium text-sm">{k.name}</div>
                                            <div className="text-[11px] text-slate-500 font-mono">{k.display}</div>
                                            <div className="text-[10px] text-slate-400 mt-0.5">Status: {k.status} · Created {new Date(k.created_at).toLocaleDateString()}</div>
                                        </div>
                                        {k.status === "active" && (
                                            <button onClick={() => revokeKey(k.api_key_id)} data-testid={`api-key-revoke-${k.api_key_id}`}
                                                    className="text-xs text-rose-600 hover:underline flex items-center gap-1"><Trash2 className="w-3 h-3" /> Revoke</button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}

            {tab === "webhook" && (
                <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 max-w-2xl">
                    <div>
                        <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Webhook URL</label>
                        <input data-testid="wh-url" value={whUrl} onChange={(e) => setWhUrl(e.target.value)}
                                placeholder="https://your-system.example.com/webhooks/payouts"
                                className="input w-full text-sm mt-1 font-mono" />
                        <p className="text-[11px] text-slate-500 mt-1">We POST a JSON event here when a payout reaches SUCCESS / FAILED / REVERSED.</p>
                    </div>
                    <div>
                        <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Payload format</label>
                        <div className="grid grid-cols-2 gap-2 mt-1">
                            {[["paygateway", "Payouts Portal", "Our clean, documented schema"], ["firstpay", "Legacy passthrough", "Vendor-shaped (for migration)"]].map(([val, lbl, sub]) => (
                                <button key={val} onClick={() => setWhFmt(val)} data-testid={`wh-fmt-${val}`}
                                        className={`p-2.5 rounded-md text-xs border transition text-left ${whFmt === val ? "border-indigo-500 bg-indigo-50" : "border-slate-200"}`}>
                                    <div className="font-semibold">{lbl}</div>
                                    <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button data-testid="wh-save" onClick={saveWebhook} disabled={busy}
                                className="btn-primary px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-1.5">
                            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save
                        </button>
                        <button data-testid="wh-rotate" onClick={rotateSecret}
                                className="px-3 py-2 rounded-md text-xs border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5">
                            <RefreshCw className="w-3.5 h-3.5" /> Rotate HMAC secret
                        </button>
                    </div>
                    {wallet?.webhook_url && (
                        <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-3">
                            Current: <code className="font-mono text-slate-700">{wallet.webhook_url}</code>
                        </div>
                    )}
                </div>
            )}

            {tab === "deliveries" && (
                <div className="bg-white border border-slate-200 rounded-xl">
                    <div className="p-3 border-b border-slate-100 flex items-center justify-between">
                        <div className="text-xs text-slate-600">Recent payouts — click a row to see every webhook delivery attempt.</div>
                        <button onClick={refresh} className="text-xs text-slate-500 hover:text-indigo-600 flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Refresh</button>
                    </div>
                    {recentPayouts.length === 0 ? (
                        <div className="p-10 text-center text-xs text-slate-500">No payouts yet.</div>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {recentPayouts.map((p) => (
                                <li key={p.payout_id}>
                                    <button data-testid={`del-row-${p.payout_id}`} onClick={() => openDeliveries(p.payout_id)}
                                            className="w-full p-3 flex items-center justify-between hover:bg-slate-50 text-left">
                                        <div className="min-w-0">
                                            <div className="text-xs font-mono truncate">{p.payout_id}</div>
                                            <div className="text-[10px] text-slate-500">to {(p.beneficiary || {}).name || "—"} · {fmtINR(p.amount)}</div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${p.status === "SUCCESS" ? "bg-emerald-100 text-emerald-800" : p.status === "FAILED" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>
                                                {p.status}
                                            </span>
                                            <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {tab === "docs" && (
                <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
                    <div className="flex items-center gap-3">
                        <BookOpen className="w-10 h-10 text-indigo-500" />
                        <div>
                            <h3 className="font-bold text-base">Developer Documentation</h3>
                            <p className="text-xs text-slate-500 mt-1">Everything your engineers need to integrate payouts.</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <a href="/docs" target="_blank" rel="noreferrer" data-testid="docs-link-public"
                                className="border border-slate-200 rounded-md p-4 hover:border-indigo-300 hover:bg-indigo-50 transition block">
                            <div className="font-semibold text-sm flex items-center gap-2"><ExternalLink className="w-4 h-4 text-indigo-600" /> Public API docs</div>
                            <div className="text-xs text-slate-500 mt-1">Open the full reference in a new tab. Share this link with your team.</div>
                        </a>
                        <a href="/docs#webhooks" target="_blank" rel="noreferrer" data-testid="docs-link-webhooks"
                                className="border border-slate-200 rounded-md p-4 hover:border-indigo-300 hover:bg-indigo-50 transition block">
                            <div className="font-semibold text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-indigo-600" /> Webhook payloads</div>
                            <div className="text-xs text-slate-500 mt-1">Schema, retry policy, HMAC signature verification.</div>
                        </a>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
                        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Quick start</div>
                        <pre className="text-[11px] font-mono text-slate-700 overflow-x-auto whitespace-pre-wrap">{`curl -X POST ${window.location.origin}/api/v1/payout \\
  -H "X-API-Key: pg_sk_live_••••" \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": 1000,
    "beneficiary": {
      "name": "John Doe",
      "account_number": "000123456789",
      "ifsc": "HDFC0001234"
    },
    "payment_type": "AUTO",
    "client_reference_id": "order_12345"
  }'`}</pre>
                    </div>
                </div>
            )}

            {/* New key modal */}
            {showKeyModal && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                    <div data-testid="new-key-modal" className="bg-white rounded-xl w-full max-w-sm p-6 space-y-4">
                        <h3 className="font-bold text-base">New API key</h3>
                        <input autoFocus value={keyName} data-testid="new-key-name"
                                onChange={(e) => setKeyName(e.target.value)}
                                placeholder="Production server" className="input w-full text-sm" />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setShowKeyModal(false)} className="px-4 py-2 rounded-md border border-slate-200 text-sm hover:bg-slate-50">Cancel</button>
                            <button data-testid="new-key-create" onClick={createKey} disabled={busy || !keyName.trim()}
                                    className="btn-primary px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2">
                                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Create
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delivery log modal */}
            {deliveryModal && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
                    <div data-testid="delivery-modal" className="bg-white rounded-xl w-full max-w-2xl p-5 space-y-3">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="font-bold text-base">Webhook deliveries</h3>
                                <p className="text-[11px] font-mono text-slate-500">{deliveryModal.payout_id}</p>
                            </div>
                            <button data-testid="delivery-close" onClick={() => setDeliveryModal(null)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
                        </div>
                        {deliveryModal.items.length === 0 ? (
                            <div className="p-6 text-center text-xs text-slate-500">No webhook deliveries yet.</div>
                        ) : (
                            <div className="divide-y divide-slate-100 border border-slate-200 rounded-md max-h-[400px] overflow-y-auto">
                                {deliveryModal.items.map((d, i) => {
                                    const ok = (d.status_code || 0) >= 200 && (d.status_code || 0) < 300;
                                    return (
                                        <div key={d.delivery_id || i} data-testid={`delivery-item-${i}`} className="p-3 text-xs space-y-1.5">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${ok ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                                                        {ok ? "DELIVERED" : "FAILED"}
                                                    </span>
                                                    {d.is_resend && <span className="px-2 py-0.5 rounded-full text-[10px] bg-amber-100 text-amber-800">RESEND</span>}
                                                    <span className="font-mono text-slate-500">HTTP {d.status_code || "—"}</span>
                                                </div>
                                                <span className="text-[10px] text-slate-500 font-mono">{new Date(d.created_at).toLocaleString()}</span>
                                            </div>
                                            <div className="text-[11px] text-slate-600">Event: <code>{d.event}</code> → <code className="break-all">{d.url}</code></div>
                                            {d.error && <div className="text-[11px] text-rose-700 bg-rose-50 p-2 rounded">Error: {d.error}</div>}
                                            {d.response_body && (
                                                <details className="text-[11px] text-slate-500">
                                                    <summary className="cursor-pointer hover:text-indigo-600">Response body</summary>
                                                    <pre className="mt-1 bg-slate-50 p-2 rounded overflow-x-auto">{String(d.response_body).slice(0, 500)}</pre>
                                                </details>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
